/* eslint-disable  react-intl/string-is-marked-for-translation */
import {createElement, Component, PropTypes} from 'rlink-react';
import Form from 'rlink-forms/redux/Form';
import {camelToConstCase, dashesToCamel} from 'rlink-utils/string';
import {elementOf} from 'rlink-utils/RlinkPropTypes';
import {Locale} from 'rlink-i18n/i18n';
import themeable from 'rlink-theme/themeable';
import Tooltip from 'rlink-tooltips/Tooltip';

// @TODO: this is a temporary necessary evil until we move
// validation logic to generic rules and then into reducers.
// The only thing allowed to acknowledge this exists is the
// CommitActiveFormFields action
export let __DO_NOT_USE__ALL_MOUNTED_INPUTS__DO_NOT_USE__ = new Set();

let inputId = 0;
/**
 * See README.md for documentation
 */
export default class InputBase extends Component {
  constructor (props, context) {
    super(props, context);
    this.labelId = `input${inputId++}`;
    this.state = {
      linkedValidations: {},
    };
  }

  static get sharedChildThemeName () {
    return 'FormTheme';
  }

  get inputType () {
    throw new Error(`Input class ${this.constructor} must define an inputType!`);
  }

  get className () {
    return `input-${this.inputType} ${(this.props.theme) ? this.props.theme[dashesToCamel(`Input-${this.inputType}`)] : ''} ${this.props.className} ${this.hasError() ? 'has-error' : ''}`.trim();
  }

  fromPropsOrContext (inputProp, required = false) {
    return this.constructor.fromPropsOrContext(
      this.props, this.context, inputProp, required);
  }

  static fromPropsOrContext (props, context, inputProp, required = false) {
    if (inputProp in props) {
      return props[inputProp];
    } else if (context.rlinkForm) {
      let contextProps = context.rlinkForm.inputProps(this.getName(props));
      if (inputProp in contextProps) {
        return contextProps[inputProp];
      }

    }

    if (required) {
      let json = obj => {
        try {
          return JSON.string(obj);
        } catch (e) {
          return String(obj);
        }
      };
      throw new Error(`Input ${this.name}(${json(props)},${json(context)}) is trying to render without a ${inputProp} prop, nor an rlinkForm context.`);
    }
  }

  static getName (props) {
    return ((props.name && props.name.length)
      ? props.name
      : props.fieldName);
  }

  get name () {
    return this.constructor.getName(this.props);
  }

  get fieldName () {
    return this.props.fieldName;
  }

  get value () {
    return this.fromPropsOrContext('value', true);
  }

  get committedValue () {
    return this.fromPropsOrContext('committedValue', true);
  }

  get changes () {
    return this.fromPropsOrContext('changes', true);
  }

  get disabled () {
    return this.fromPropsOrContext('disabled', true);
  }

  get validationProblems () {
    return this.fromPropsOrContext('validationProblems', true);
  }

  get onChange () {
    return this.fromPropsOrContext('onChange', true);
  }

  get onFocus () {
    return this.fromPropsOrContext('onFocus');
  }

  get onBlur () {
    return this.fromPropsOrContext('onBlur');
  }

  get onChangeDidCommit () {
    return this.fromPropsOrContext('onChangeDidCommit', true);
  }

  get onChangeWillCommit () {
    return this.fromPropsOrContext('onChangeWillCommit');
  }

  get onValidate () {
    return this.fromPropsOrContext('onValidate', true);
  }

  get required () {
    return this.fromPropsOrContext('required');
  }

  get confirmationOf () {
    return this.fromPropsOrContext('confirmationOf');
  }

  get label () {
    return this.fromPropsOrContext('label');
  }

  get help () {
    return this.fromPropsOrContext('help');
  }

  get autoComplete () {
    return this.fromPropsOrContext('autoComplete');
  }

  get autoFocus () {
    return this.fromPropsOrContext('autoFocus');
  }


  validate (activeValidationLinks = {}) {
    let validationEvent = {
      value: this.normalizeValue(this.value),
      valid: true,
      problems: [],
    };

    this.addValidationProblems(validationEvent);


    if (this.inputRef
        && this.inputRef.checkValidity
        && !this.inputRef.checkValidity()) {
      validationEvent.problems.push('INVALID');
    }

    if (validationEvent.problems.length > 0) {
      validationEvent.valid = false;
    }

    if (this.onValidate) {
      this.onValidate(validationEvent);
    }



    // run any linked validations
    for (let key in this.state.linkedValidations) {
      // avoid circular validation
      if ( !(key in activeValidationLinks) ) {
        let input = this.state.linkedValidations[key];

        // add self to other component's validation check
        activeValidationLinks[this.fieldKey()] = true;
        input.validate(activeValidationLinks);

      }
    }

    return validationEvent;
  }

  fieldKey () {
    //eslint-disable-next-line react/prop-types
    let key = this.name || this.props.key;
    if (key) {
      return key;
    } else {
      throw new Error('You must provide a way to identify each input field - either a name or key prop.');
    }
  }

  addValidationProblems (ev) {
    if (this.required && !this.hasValue(this.value)) {
      return this.addProblem(ev, 'REQUIRED');
    }

    if (this.confirmationOf) {
      let confirmRef = this.confirmationOf;
      if (confirmRef.normalizeValue(confirmRef.value)
          !== ev.value) {
        this.addProblem(ev, 'DOES_NOT_MATCH');
      }

    }
  }

  addProblem (ev, problemLabel) {
    let fieldSpecificMsg = [
      'PROBLEM', camelToConstCase(this.fieldName), problemLabel,
    ].join('_');

    let genericMsg = [
      'PROBLEM', problemLabel,
    ].join('_');

    let problemText;
    if (this.props.loc) {
      if (fieldSpecificMsg in this.props.loc) {
        problemText = this.props.loc[fieldSpecificMsg];
      } else if (genericMsg in this.props.loc) {
        problemText = this.props.loc[genericMsg];
      } else {
        console && console.warn && console.warn('Missing string!', genericMsg, 'or', fieldSpecificMsg);
      }
    } else {
      problemText = fieldSpecificMsg;// @todo: remove me when new loc system is up
    }

    ev.problems.push(`${problemText} `);
    ev.valid = false;
  }

  hasError () {
    return (this.validationProblems
      && this.validationProblems.length > 0);
  }

  //eslint-disable-next-line class-methods-use-this
  normalizeValue (value) {
    return value;
  }

  linkValidation (component, withBackLink = true) {
    // should this be done via props.actions at the higher level instead?
    // we could consider this a private concern between inputs...
    // (the assumption being no other components ever have a good
    // reason to "look into" other components' validation
    // links...)
    let linkedValidations = this.state.linkedValidations;
    linkedValidations[component.fieldKey()] = component;
    this.setState({linkedValidations});
    if (withBackLink) {
      component.linkValidation(this, false);
    }
  }


  unlinkValidation (component, withBackLink = true) {
    let linkedValidations = this.state.linkedValidations;
    delete linkedValidations[component.fieldKey()];
    this.setState({linkedValidations});
    if (withBackLink) {
      component.unlinkValidation(this, false);
    }
  }


  componentDidMount () {
    if (this.confirmationOf) {
      this.linkValidation(this.confirmationOf);
    }
    __DO_NOT_USE__ALL_MOUNTED_INPUTS__DO_NOT_USE__.add(this);
    if (this.context.rlinkForm) {
      this.context.rlinkForm.inputs.add(this);
    }
    //@TODO remove me if everyone transitioned away form
    //... inputProps
    this._inputWillUnmount = false;
  }


  componentWillUnmount () {
    __DO_NOT_USE__ALL_MOUNTED_INPUTS__DO_NOT_USE__.delete(this);
    if (this.context.rlinkForm) {
      this.context.rlinkForm.inputs.delete(this);
    }
    //@TODO remove me if everyone transitioned away form
    //... inputProps
    this._inputWillUnmount = true;
  }

  componentWillReceiveProps (nextProps, nextContext) {
    let nextConfirmationOf = this.constructor.fromPropsOrContext(nextProps, nextContext, 'confirmationOf');
    if (!this.confirmationOf && nextConfirmationOf) {
      this.linkValidation(nextConfirmationOf);
    }
    if (this.confirmationOf && !nextConfirmationOf) {
      this.unlinkValidation(this.confirmationOf);
    }
  }

  componentDidUpdate (prevProps, prevState, prevContext) {
    let prevValue = this.constructor.fromPropsOrContext(prevProps, prevContext, 'value');
    if (this.changes && this.changes.length > 0
        && (this.normalizeValue(prevValue)
          !== this.normalizeValue(this.value))) {
      // validate-able value actually changed
      this.validate();
    }
  }


  handleChange (ev) {
    if (this.onChange) {
      let value = 'value' in ev ? ev.value : ev.target.value;
      let event = 'event' in ev ? ev.event : ev;
      let changes = 'changes' in ev ? ev.changes : this.valueChanges(event, value);
      return this.onChange({
        event,
        value,
        changes,
      });
    }
  }

  handleFocus (ev) {
    let value = this.normalizeValue(this.value);
    if (this.onFocus) {
      this.onFocus({
        event: ev,
        value,
      });
    }
  }

  handleBlur (ev) {
    if (this.onBlur) {
      this.onBlur({
        event: ev,
        value: this.value,
      });
    }
    this.fireCommitValue(ev, this.value, ev.changes, false);

  }

  //eslint-disable-next-line class-methods-use-this
  valueChanges (event, value) {
    return [
      ['ChangeContentValue', {newValue: value}],
    ];
  }


  // Note on delay: the delay is there to ensure change
  // fires before ChangeWillCommit, Validate etc. for
  // components without a blur event. So always set it
  // to false when onBlur is relevant, always set it to
  // true otherwise.
  fireCommitValue (ev, value, changes = null, delay = true) {


    // no value was committed OR the committed value is actually different        OR we were explicitly passed changes
    let valueDidChange = this.committedValue !== this.normalizeValue(value) || changes !== undefined || changes !== null;

    let fireEvents = () => {
      if (valueDidChange) {

        if (!changes) {
          changes = this.valueChanges(ev, value);
        }

        if (this.onChangeWillCommit) {
          this.onChangeWillCommit({
            ev,
            target: this,
            rawValue: value,
            value: this.normalizeValue(value),
            changes,
          });
        }

      }
      let validationEvent = this.validate();

      if (valueDidChange
          && (!validationEvent.problems
            || validationEvent.problems.length === 0)
          && this.onChangeDidCommit) {

        this.onChangeDidCommit({
          ev,
          target: this,
          rawValue: value,
          value: this.normalizeValue(value),
          changes,
        });

      }
    };
    if (delay) {
      setTimeout(fireEvents, 10); // to allow onChange to have updated this.props.value
    } else {
      // but this is not needed for blur-supporting inputs
      fireEvents();
    }

  }

  render () {
    return (
      <Tooltip content={this.props.tooltip}>
        <div className={`control ${this.className}`}>
          <label htmlFor={this.labelId}>
            {this.renderLabel()}
            {this.renderError()}
          </label>
          {this.renderHelp()}
          {this.renderInput()}
        </div>
      </Tooltip>
    );
  }

  renderLabel () {
    if (!this.label) {
      return '';
    }
    if (typeof this.label === 'object'
        && this.label !== null
        && '__html' in this.label) {
      return (<span
        className="labelText"
        dangerouslySetInnerHTML={this.label} />);
    } else {
      return (<span className="labelText">
        {this.label}
      </span>);
    }
  }

  renderError () {
    if (this.hasError()) {
      return <span className="field-error">
        {this.validationProblems.map((prob, idx) => <span key={idx} className="field-problem">{prob}</span>)}
      </span>;
    } else {
      return '';
    }
  }

  renderHelp () {
    if (!this.help) {
      return '';
    }
    if (typeof this.help === 'object'
        && this.help !== null
        && '__html' in this.help) {
      return (<div
        className="help-text"
        dangerouslySetInnerHTML={this.help} />);
    } else {
      return (<small className="help-text">
        {this.help}
      </small>);
    }


  }
}
/* eslint-disable react/no-unused-prop-types */
InputBase.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  fieldName: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChangeWillCommit: PropTypes.func,
  onChangeDidCommit: PropTypes.func,
  disabled: PropTypes.bool,
  loc: PropTypes.instanceOf(Locale),
  onValidate: PropTypes.func,
  validationProblems: PropTypes.arrayOf(
    PropTypes.node),
  required: PropTypes.bool,
  confirmationOf: PropTypes.instanceOf(InputBase),
  autoComplete: PropTypes.oneOf([
    'off',
    'on',
    'name',
    'honorific-prefix',
    'given-name',
    'additional-name',
    'family-name',
    'honorific-suffix',
    'nickname',
    'email',
    'username',
    'new-password',
    'current-password',
    'organization-title',
    'organization',
    'street-address',
    'address-line1',
    'address-line2',
    'address-line3',
    'address-level1',
    'address-level2',
    'address-level3',
    'address-level4',
    'country',
    'country-name',
    'postal-code',
    'cc-name',
    'cc-given-name',
    'cc-additional-name',
    'cc-family-name',
    'cc-number',
    'cc-exp',
    'cc-exp-month',
    'cc-exp-year',
    'cc-csc',
    'cc-type',
    'transaction-currency',
    'transaction-amount',
    'language',
    'bday',
    'bday-day',
    'bday-month',
    'bday-year',
    'sex',
    'tel',
    'url',
    'photo',
  ]),
  autoFocus: PropTypes.bool,
  value: PropTypes.any,
  changes: PropTypes.arrayOf(PropTypes.array),
  committedValue: PropTypes.any,
  label: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.shape({
      __html: PropTypes.string,
    }),
  ]),
  help: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.shape({
      __html: PropTypes.string,
    }),
  ]),
  tooltip: PropTypes.node,
};
/* eslint-enable react/no-unused-prop-types */
InputBase.defaultProps = {
  className: '',
};

InputBase.contextTypes = {
  rlinkForm: elementOf(Form),
};


themeable(InputBase, {

});



// WEBPACK FOOTER //
// ./lib/rlink-forms/inputs/InputBase.jsx